/**
 * TRAC-732 — the live-person funnel, from the only place that can see most of it.
 *
 * The service cannot tell you whether asking for a human worked. Hundreds of connect
 * calls succeeded on the server while customers were being shown "we couldn't reach a
 * person", and in one case a rep arrived after the apology had already been given. So
 * three of the moments that matter are only visible here, in the browser: that a rep
 * really turned up, that the customer was shown the apology, and that they gave up
 * waiting.
 *
 * The clock starts when they ask, not when the server accepts, because the wait a
 * customer actually sits through starts with the button press.
 *
 * The attempt belongs to the SESSION, not the page. The chat panel follows a visitor
 * from page to page and a handoff started on one page is resumed on the next -- on
 * TR.com, and on a phone, browsing while you wait is the normal way to wait. So the
 * ask is written down beside the session and read back when the same session comes up
 * on a later page: the rep who arrives there is still counted, and their wait is still
 * measured from the press. Leaving a page while waiting -- and, on a phone, putting the
 * browser in the background, which is the last thing the page hears before a swipe-kill
 * -- is reported as it happens (it is the only timestamp we get for "how long before
 * they gave up"), but it is provisional -- if the same attempt goes on to anything
 * else, the read-back lets the later moment win.
 *
 * Nothing in here may throw or block. This is measurement wrapped around the highest
 * stakes moment in the product, and a slow collector must never be the reason somebody
 * fails to reach a human.
 */
import { getBackendApiUrl } from '../config/backend';
import { logger } from '../utils/logger';
import { getTrackingPageKind } from './interactionTrackingService';
import { chatAgentService } from './RealChatAgentService';
import type { HandoffInitiator } from './handoffInitiator';
import {
  OPEN_ATTEMPT_STORAGE_KEY,
  clearOpenAttempt,
  readOpenAttempt,
  writeOpenAttempt,
  type OpenHandoffAttempt,
} from './handoffOpenAttempt';

type FunnelEvent =
  | 'handoff_requested'
  | 'handoff_declined'
  | 'handoff_joined'
  | 'handoff_failed'
  | 'handoff_abandoned'
  | 'handoff_ended'
  | 'handoff_slow';

interface Extra {
  reason?: string;
  department?: string;
  /** Set on the terminal events; computed here so every one is measured from the ask. */
  waitedMs?: number;
  /** When this happened, if not now. Two events in the same instant must not tie. */
  at?: string;
  /** The live rep's display name, on the moments that have one (joined, ended). */
  specialist?: string;
  /** TRAC-1212: who started this attempt (on the ask, and on a decline the browser files). */
  initiatedBy?: HandoffInitiator;
  /** TRAC-1212: on a closed_hours decline, when we reopen (ISO 8601 with the ET offset). */
  nextOpen?: string;
}

type TokenFn = () => Promise<string | undefined>;

/** Epoch ms of the press. Epoch, not performance.now(): it has to survive a page change. */
let askedAt: number | null = null;
let askedFor: string | undefined;
/**
 * TRAC-1049: whether `askedFor` is the team the BACKEND routed to, rather than the one the
 * customer picked on the way in. Only a confirmed team may be named on screen.
 */
let askedForConfirmed = false;
let sessionId: string | null = null;
let getToken: TokenFn | null = null;
/**
 * The last token the lookup handed back. Kept so the one send that cannot wait for a
 * promise -- the report from a page being torn down -- still goes out signed. The
 * collector refuses anything unsigned (401), so an unsigned leaving report is not a
 * degraded report, it is no report: the tab-close give-up would simply never be counted,
 * and the success rate would flatter itself by exactly the number this exists to catch.
 */
let lastToken: string | undefined;
/** True between asking and either a rep arriving or the attempt ending. */
let waiting = false;
let armed = false;
/**
 * True once THIS departure -- the page going to the background -- has been reported,
 * until the page is seen again. One report per departure, not one per tab flip.
 */
let awayReported = false;
/**
 * TRAC-1007: this attempt's slow wait has been reported -- here, on an earlier page, or by
 * another tab on the same conversation (read back from the stored attempt).
 */
let slowReported = false;
/**
 * TRAC-1032: moments that happened before the conversation had a session. A customer
 * who opens the chat and presses the button first has no id yet -- the connect starts
 * the session, and the id arrives a moment later. Nothing can be filed without one, and
 * dropping the ask would drop the denominator for exactly the customers the button
 * exists for. Held, stamped with when they happened, and sent the moment init brings
 * an id. Bounded: a page that never gets one must not grow a queue forever.
 */
let pending: Array<{ event: FunnelEvent; extra: Extra }> = [];
const PENDING_MAX = 20;

function remember(token?: string): string | undefined {
  if (token) lastToken = token;
  return token;
}

/**
 * The open attempt, written beside the session so the next page can pick it up. One
 * record, overwritten by each new ask; removed by every ending except leaving the page,
 * which is the one ending that might not be one.
 *
 * TRAC-1049: it is in localStorage, so it is also the only record of the open ask that a
 * SECOND TAB of the same conversation can read — see handoffOpenAttempt.ts, which owns its
 * shape so the service can read it too without importing this module.
 */
const storage = (): Storage | null => {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
};

function writeAttempt(slowAt?: number, confirmed?: boolean): void {
  if (!sessionId || askedAt === null) return;
  writeOpenAttempt(storage(), OPEN_ATTEMPT_STORAGE_KEY, {
    sessionId,
    askedAt,
    department: askedFor,
    departmentConfirmed: confirmed ?? askedForConfirmed,
    slowAt,
  });
}

function forgetAttempt(): void {
  clearOpenAttempt(storage(), OPEN_ATTEMPT_STORAGE_KEY);
}

function readAttempt(session: string): OpenHandoffAttempt | null {
  return readOpenAttempt(storage(), OPEN_ATTEMPT_STORAGE_KEY, session);
}

/**
 * TRAC-928: which page they were on, so the funnel can say WHERE asking goes wrong and
 * not just how often. The kind is whatever the loader classified -- the in-chat
 * tracker already holds it, and this reads that rather than keeping a copy. The URL
 * is the PARENT page's, from the context the host posted; never this frame's own
 * location, which is the embed's URL on every page and would answer the question with
 * the same string every time. No context, no URL: an empty answer is better than a
 * wrong one. Read at send time, not carried with the attempt -- an attempt can cross
 * pages, and the collector keys the page off the ask.
 */
function parentPageUrl(): string | undefined {
  try {
    const url = chatAgentService.getPageContext()?.page_url;
    return typeof url === 'string' && url ? url : undefined;
  } catch {
    return undefined;
  }
}

function send(event: FunnelEvent, extra: Extra = {}, onUnload = false): void {
  if (!sessionId) {
    // TRAC-1032: no id yet -- hold it for the one that is coming. Not on the way out:
    // a page being torn down has no later in which an id could arrive.
    if (!onUnload && pending.length < PENDING_MAX) {
      pending.push({ event, extra: { ...extra, at: extra.at ?? new Date().toISOString() } });
    }
    return;
  }
  const body = JSON.stringify({
    sessionId,
    event,
    occurredAt: extra.at ?? new Date().toISOString(),
    reason: extra.reason,
    waitedMs: extra.waitedMs,
    department: extra.department,
    specialist: extra.specialist,
    initiatedBy: extra.initiatedBy,
    nextOpen: extra.nextOpen,
    pageKind: getTrackingPageKind() ?? undefined,
    url: parentPageUrl(),
  });

  const post = (token?: string) => {
    try {
      void fetch(`${getBackendApiUrl()}/api/analytics/interaction-events/agent-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body,
        // Only on the way out. The browser discards ordinary requests from a page it is
        // tearing down, which is exactly the case that tells us somebody gave up. Not
        // used otherwise: this queue has been seen to stall under bursts, and the other
        // events have no navigation to race.
        keepalive: onUnload,
      }).catch(() => {});
    } catch {
      /* measurement never breaks the thing it measures */
    }
  };

  if (!getToken) { post(lastToken); return; }
  // On the way out there is no time to await anything -- the page may be gone before a
  // promise resolves -- so an unload send goes immediately, signed with the token we
  // already hold. There is always one by then: the ask that put us in the waiting state
  // fetched it, and init primes it before that.
  if (onUnload) { post(lastToken); return; }
  // A lookup that comes back empty (not throwing -- just no token yet) must not strip a
  // send that a token already in hand could sign.
  getToken().then((t) => post(remember(t) ?? lastToken)).catch(() => post(lastToken));
}

/** Call once, as early as the chat knows who it is. Safe to call repeatedly. */
export function initHandoffFunnel(session: string, tokenFn: TokenFn): void {
  sessionId = session;
  getToken = tokenFn;
  // Warm the token now, while there is time to await it, so the leaving report -- which
  // has none -- never goes out unsigned.
  tokenFn().then(remember).catch(() => {});
  // TRAC-1032: an ask made before there was an id. Put the open attempt on record under
  // the id it now has -- a page change mid-wait must find it -- and file what was held,
  // in order, each at the moment it actually happened.
  if (waiting && askedAt !== null) writeAttempt();
  if (pending.length) {
    const held = pending;
    pending = [];
    for (const p of held) send(p.event, p.extra);
  }
  // The same session coming up on a new page with an ask still open: pick the attempt
  // back up, clock and all, so a rep arriving here counts and the wait reads from the
  // press. A record for some OTHER session is left alone rather than dropped -- the
  // chat can report a provisional id before it settles on the real one, and dropping
  // the record on the first would lose the attempt before the second could claim it.
  rearmFromStorage();
  if (armed || typeof window === 'undefined') return;
  armed = true;

  // Closing the tab while still waiting is a give-up, and a common one -- somebody who
  // has been waiting a while is more likely to leave than to hunt for a cancel button.
  // Counting only the button would flatter the number by hiding the worst cases.
  //
  // pagehide, not beforeunload: beforeunload does not fire reliably on mobile, and this
  // is the half of the audience most likely to be doing exactly this.
  //
  // The record is deliberately KEPT here. Leaving the page is provisional: on the shop
  // site the panel comes back on the next page with the same attempt still open, and if
  // that page ends in a rep arriving, that is the moment that counts.
  const onLeave = () => {
    if (!waiting) return;
    waiting = false;
    // A page unloading from the background already said so when it went there.
    if (awayReported) return;
    send('handoff_abandoned', { reason: 'left_the_page', waitedMs: sinceAsk() }, true);
  };
  window.addEventListener('pagehide', onLeave);

  // On a phone the common way to give up is neither a button nor a tab close: it is
  // Home, then swiping the browser away in the switcher. Nothing fires on the kill --
  // no pagehide, no unload; the only signal the page ever gets is the moment it went to
  // the background. So THAT is reported, provisionally: the attempt stays open, waiting
  // stays true, the record stays put. Somebody who merely checked a text and came back
  // to a rep is still counted as reached -- the read-back lets the later event win --
  // while somebody who never came back is counted as having left, which is the truth
  // and the number that would otherwise be missing. Same keepalive path as unload:
  // a backgrounded page can be frozen before an ordinary request gets out.
  const onAway = () => {
    if (!waiting || awayReported) return;
    awayReported = true;
    send('handoff_abandoned', { reason: 'left_the_page', waitedMs: sinceAsk() }, true);
  };
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') onAway();
    else awayReported = false;         // back again: the next departure is its own report
  };
  document.addEventListener('visibilitychange', onVisibility);
  // Chrome fires this before discarding a background tab; the discard itself is silent.
  document.addEventListener('freeze', onAway);

  // The back button (and a swipe back on a phone) can bring THIS page back from the
  // browser's back/forward cache with its script state intact: pagehide fired, waiting
  // went false, and init does not run again because nothing reloaded. Without this,
  // a rep arriving on the restored page would be dropped. Same re-arm as a fresh page.
  const onReturn = (e: Event) => {
    if ((e as PageTransitionEvent).persisted) rearmFromStorage();
  };
  window.addEventListener('pageshow', onReturn);
}

/** Pick an open attempt for the current session back up: clock, department, waiting. */
function rearmFromStorage(): void {
  if (!sessionId) return;
  const open = readAttempt(sessionId);
  if (!open) return;
  askedAt = open.askedAt;
  askedFor = open.department;
  askedForConfirmed = open.departmentConfirmed === true;
  waiting = true;
  awayReported = false;
  slowReported = open.slowAt !== undefined;
}

function sinceAsk(): number | undefined {
  return askedAt === null ? undefined : Math.max(0, Math.round(Date.now() - askedAt));
}

/**
 * They asked. Starts the clock the whole funnel is measured against.
 *
 * TRAC-1212: `initiatedBy` says who started it — the shopper with a button, the shopper in
 * words to the AI, or the AI's own offer they accepted. Optional so a caller that cannot
 * tell records nothing rather than a guess.
 */
export function handoffRequested(department?: string, initiatedBy?: HandoffInitiator, reason?: string): void {
  askedAt = Date.now();
  askedFor = department;
  askedForConfirmed = false;   // TRAC-1049: a pick, not yet the team the backend routed to
  waiting = true;
  awayReported = false;
  slowReported = false;
  writeAttempt();
  // TRACI-865: `reason` says how an AI offer connected (soft_timer, soft_click, ...).
  // TRACI-868: stamped with `askedAt` itself, not a second clock read. A second read can land
  // a millisecond later, on the very instant handoffAskedWhileClosed gives its decline.
  send('handoff_requested', {
    department, initiatedBy, at: new Date(askedAt).toISOString(), ...(reason ? { reason } : {}),
  });
}

/**
 * TRAC-1049: the backend has said which team actually got this chat. Recorded on the SHARED
 * attempt, because the tab that asked is not the only tab showing this conversation: a second
 * tab has no record of the ask at all and would otherwise have nothing to name but "a Tire
 * Expert", under a heading the first tab has already filled in with the team.
 *
 * Confirmed, not picked, on purpose — the intended team can be closed or busy and the chat
 * rerouted, and naming a team optimistically is the thing handoffResume's generic heading
 * exists to avoid. Every tab now names a team at the same moment and names the same one.
 *
 * Reported nowhere: the funnel's own events keep carrying the team as before (`askedFor` is
 * what they read, and the backend's answer is the better value for them too).
 */
export function handoffDepartmentConfirmed(department?: string | null): void {
  if (!department) return;
  askedFor = department;
  askedForConfirmed = true;
  if (!waiting || askedAt === null) return;
  // Keep whatever the record already says about the still-trying message: another tab may
  // have shown it, and re-writing the record without it would let a second one be shown.
  const open = sessionId ? readAttempt(sessionId) : null;
  writeAttempt(open && open.askedAt === askedAt ? open.slowAt : undefined, true);
}

/**
 * TRAC-1049: the team on the attempt still waiting for a rep, for a tab that has no record of
 * the ask — or null when nobody is waiting, or when no team has been named. Re-read from
 * storage the way `handoffAskedAt` is, so a tab that did not press the button gets an answer.
 *
 * `confirmed` is false while this is only the customer's pick. Nothing may NAME an unconfirmed
 * team on screen; the still-trying line may still use it for the phone number it reserves
 * space for, which is what the tab that asked has always done.
 */
export function handoffAskedDepartment(): { department: string | null; confirmed: boolean } {
  if (waiting && askedAt !== null) {
    // A tab that re-armed from storage before the backend answered holds the PICK in memory;
    // the tab that asked later writes the confirmed team to the shared record. Adopt it, or
    // this tab would name no team for the rest of the wait while the other names one.
    if (!askedForConfirmed && sessionId) {
      const open = readAttempt(sessionId);
      if (open && open.askedAt === askedAt && open.departmentConfirmed && open.department) {
        askedFor = open.department;
        askedForConfirmed = true;
      }
    }
    return { department: askedFor ?? null, confirmed: askedForConfirmed };
  }
  const open = sessionId ? readAttempt(sessionId) : null;
  return {
    department: open?.department ?? null,
    confirmed: open?.departmentConfirmed === true,
  };
}

/**
 * TRAC-1049: the team the backend confirmed for the open attempt, or null. The only team any
 * tab may put on screen by name — see handoffDepartmentConfirmed.
 */
export function handoffConfirmedDepartment(): string | null {
  const { department, confirmed } = handoffAskedDepartment();
  return confirmed ? department : null;
}

/**
 * TRAC-1007: when the attempt still waiting for a rep was asked for (epoch ms), or null when
 * nobody is waiting. The chat's one-minute "still trying" message runs on this clock, so it
 * is measured from the press and survives a page change: the stored attempt is read back
 * when this page did not see the ask itself.
 */
export function handoffAskedAt(): number | null {
  if (waiting && askedAt !== null) return askedAt;
  const open = sessionId ? readAttempt(sessionId) : null;
  return open ? open.askedAt : null;
}

/**
 * TRAC-1007: a minute in and nobody has picked up, so the customer was told we are still
 * trying. Not an ending: the attempt stays open, and a rep who joins afterwards is still the
 * success, measured from the press. Once per attempt, across pages and tabs -- the stored
 * attempt carries the fact -- so the count is waits that got this long, not pages that saw it.
 */
export function handoffSlow(department?: string): void {
  if (!waiting || slowReported) return;
  const open = sessionId ? readAttempt(sessionId) : null;
  slowReported = true;
  if (open && open.askedAt === askedAt && open.slowAt !== undefined) return; // another tab said so
  writeAttempt(Date.now());
  send('handoff_slow', { department: department ?? askedFor, waitedMs: sinceAsk() });
}

/**
 * They pressed the button while the desk was shut, and were shown the hours. The
 * button does not ask the server when it already knows the answer, so without this the
 * funnel would never hear of anybody who wanted a person after hours -- and that is the
 * staffing signal the hour-of-week chart exists for. Recorded as the server would have
 * recorded it had it been asked: a request, declined. The decline is stamped one
 * millisecond after the ask so the two never tie when the attempts are put in order.
 * Today's availability check only ever says "closed_hours"; the no_agents reason is
 * kept for parity with the server, which still declines that way when the desk is busy.
 *
 * TRAC-1212: the only way here is the button, so the shopper started it; a closed_hours
 * decline also carries when we reopen (`nextOpen`, from the availability answer), the
 * same field the server's own decline carries.
 */
export function handoffAskedWhileClosed(state: 'closed_hours' | 'closed_no_agents' | string, nextOpen?: string): void {
  const initiatedBy: HandoffInitiator = 'shopper_button';
  handoffRequested(undefined, initiatedBy);
  const reason = state === 'closed_hours' ? 'closed_hours' : 'no_agents';
  const at = new Date((askedAt ?? Date.now()) + 1).toISOString();
  waiting = false;
  forgetAttempt();
  send('handoff_declined', {
    reason, at, waitedMs: 0, initiatedBy,
    ...(reason === 'closed_hours' && nextOpen ? { nextOpen } : {}),
  });
}

/** A rep is really in the conversation. The only outcome that counts as success. */
export function handoffJoined(department?: string, specialist?: string): void {
  if (!waiting) return;              // a transfer mid-conversation is not a new connect
  waiting = false;
  forgetAttempt();
  send('handoff_joined', { department: department ?? askedFor, waitedMs: sinceAsk(), specialist });
}

/** They were shown the apology. */
export function handoffFailed(reason: string, department?: string): void {
  waiting = false;
  forgetAttempt();
  send('handoff_failed', { reason, department: department ?? askedFor, waitedMs: sinceAsk() });
}

/** They gave up before anyone arrived — the cancel button, rather than leaving. */
export function handoffAbandoned(reason = 'cancelled'): void {
  if (!waiting) return;
  waiting = false;
  forgetAttempt();
  send('handoff_abandoned', { reason, waitedMs: sinceAsk() });
}

/** Stop the clock without calling it a failure — used when the desk is shut. */
export function handoffSettled(): void {
  waiting = false;
  forgetAttempt();
}

/**
 * Turn whatever the connect threw into a short, groupable reason.
 *
 * This is the distinction the whole investigation stalled on. The service returned
 * success while customers saw the apology, so the interesting question is whether the
 * browser got an ANSWER it disliked (an HTTP status — our fault, findable in the server
 * logs) or never got an answer at all (a rejected fetch — blocked, cross-origin, or the
 * frame torn down, which leaves no server-side trace whatsoever). Collapsing both into
 * "it failed" is what left nobody able to say which had happened.
 */
export function describeConnectFailure(err: unknown): string {
  // TRAC-1032: the connect has two stages now. The same "http_503" means the desk (or our
  // side of it) refused when it came from the ask, and that the conversation never
  // existed when it came from starting the session. Grouped apart so the dashboard can
  // tell a desk problem from a session one.
  const stage = err && typeof err === 'object' ? (err as { stage?: string }).stage : undefined;
  const reason = describeFailure(err);
  return stage === 'session' ? `session_${reason}` : reason;
}

function describeFailure(err: unknown): string {
  if (err && typeof err === 'object') {
    // `httpStatus` is what startHandoff actually tags its errors with (0 for a network
    // rejection, which is not a status and must fall through to the message below);
    // `status` / `response.status` cover anything thrown by a fetch wrapper.
    const e = err as { httpStatus?: number; status?: number; response?: { status?: number } };
    const status = e.httpStatus || e.status || e.response?.status;
    if (typeof status === 'number' && status > 0) return `http_${status}`;
    const name = (err as { name?: string }).name;
    if (name === 'AbortError') return 'aborted';
    if (name === 'TimeoutError') return 'timed_out';
    const message = String((err as { message?: string }).message || '');
    // A bare "Failed to fetch" is the browser refusing to tell us more: no status, no
    // body. Blocked, cross-origin, offline, or the frame went away mid-request.
    if (/failed to fetch|networkerror|load failed/i.test(message)) return 'no_response';
    if (message) return 'error_' + message.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40);
  }
  return 'unknown';
}

/** TRACI-1111: why the backend ended a live chat, as it says on the 'ended' event. Only known
 * causes pass, so a server value can never become free text in analytics. */
const BACKEND_END_REASONS = new Set([
  'rep_closed', 'rep_soft_end', 'customer_idle', 'customer_left_intent', 'lp_connection_lost',
  'lp_closed_while_disconnected', 'resume_dropped', 'bridge_crash', 'unrehydratable',
  'unjoined', // CPOC-150: no rep joined the Connect chat in time
]);

export function handoffEndReason(reason?: string): string {
  return reason && BACKEND_END_REASONS.has(reason) ? reason : 'rep_left';
}

/** A conversation that really connected has finished. */
export function handoffEnded(reason: string, specialist?: string): void {
  waiting = false;
  forgetAttempt();
  send('handoff_ended', { reason, specialist });
}

/** Test seam: a fresh page AND a fresh visitor (the open-attempt record goes too). */
export function _resetHandoffFunnel(): void {
  _newPage();
  forgetAttempt();
}

/** Test seam: what a navigation does -- module state gone, storage kept. */
export function _newPage(): void {
  askedAt = null; askedFor = undefined; askedForConfirmed = false;
  sessionId = null; getToken = null; lastToken = undefined;
  waiting = false; armed = false; awayReported = false; slowReported = false; pending = [];
}

export const _internals = {
  isWaiting: () => waiting,
  log: logger,
};
